import { createSlice } from '@reduxjs/toolkit';

// Positions arrive from several places at once: the paged list brings trimmed
// ones, the open status card fetches a full one, and the socket streams both.
// Ids only ever grow for a device, so a newer id always replaces, an older
// one is dropped, and the same id merges attributes so a trimmed copy never
// strips the full one already held.
const mergePosition = (positions, position) => {
  const existing = positions[position.deviceId];
  if (!existing || position.id > existing.id) {
    positions[position.deviceId] = position;
  } else if (position.id === existing.id) {
    positions[position.deviceId] = {
      ...existing,
      ...position,
      attributes: { ...existing.attributes, ...position.attributes },
    };
  }
};

const { reducer, actions } = createSlice({
  name: 'session',
  initialState: {
    server: null,
    user: null,
    socket: null,
    positions: {},
    positionsBuffer: [],
    history: {},
    agentLocation: null,
    // What the tiered socket should push: the device ids whose list rows are
    // on screen, the one with an open status card, the user's distance unit
    // for the travel bands, and the filter the marker stream should honour.
    subscription: {
      rows: [], full: [], distanceUnit: null, filter: null,
    },
    // Fleet-wide bucket sizes for the filter menu, fetched once and then
    // pushed over the socket whenever they change.
    counts: null,
    // Devices the stream reported as no longer matching the filter. Each
    // batch replaces the list and bumps the sequence, so a reader can tell
    // a new batch from the last one it handled.
    removedDevices: { seq: 0, ids: [] },
    // Devices the stream announced as newly visible: created in or moved
    // into the company while the socket was open. Same shape as the above.
    addedDevices: { seq: 0, ids: [] },
    // Devices whose marker came in with a different status from the one the
    // unfiltered marker index held. Same shape as the above.
    statusChanges: { seq: 0, ids: [] },
  },
  reducers: {
    updateServer(state, action) {
      state.server = action.payload;
    },
    updateUser(state, action) {
      state.user = action.payload;
    },
    updateSocket(state, action) {
      state.socket = action.payload;
    },
    updateAgentLocation(state, action) {
      state.agentLocation = action.payload;
    },
    updatePositions(state, action) {
      action.payload.forEach((position) => {
        state.positionsBuffer.push(position);
      });
    },
    // Positions fetched over REST are wanted on screen now, not after the
    // next buffer flush.
    setPositions(state, action) {
      action.payload.forEach((position) => mergePosition(state.positions, position));
    },
    flushBufferToPositions(state) {
      state.positionsBuffer.forEach((position) => mergePosition(state.positions, position));
      state.positionsBuffer = [];
    },
    updateSubscription(state, action) {
      state.subscription = { ...state.subscription, ...action.payload };
    },
    updateCounts(state, action) {
      state.counts = action.payload;
    },
    updateRemovedDevices(state, action) {
      state.removedDevices = { seq: state.removedDevices.seq + 1, ids: action.payload };
    },
    updateAddedDevices(state, action) {
      state.addedDevices = { seq: state.addedDevices.seq + 1, ids: action.payload };
    },
    updateStatusChanges(state, action) {
      state.statusChanges = { seq: state.statusChanges.seq + 1, ids: action.payload };
    },
  },
});

export { actions as sessionActions };
export { reducer as sessionReducer };
